import { defineComponent as _defineComponent } from 'vue'
import Vue, { computed, ref } from 'vue';

import type { TitleDetail } from '@/interfaces/title-details-graphql';
import type { CatalogueItem } from '@/helpers/promotion-helper';

import { ThumborOptions } from '@/filters/thumbor';

import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { mrcObservableOptions } from '@/helpers/composables/useBatchImpressionTracking';

import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { Offer, TitleFilter } from '@/@types/graphql-types';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { QTVSimilarTitle, useQTV } from '@/features/buybox/composables/useQTV';
import type { AdServerQtvCreativeFragment } from '@/features/ad-server/graphql/fragments/AdServerQTVCreative.fragment';
import ClickoutAnchor from '@/components/nav/links/ClickoutAnchor.vue';
import { type ClickoutUrl } from '@/helpers/clickout-helper';
import { useTitlePlayerModal } from '@/features/title-player/composables/useTitlePlayerModal';

import { qtvProviderType } from '@/features/buybox/helpers/quality-tv-data';
import DisclaimerButton from '@/features/buybox/components/DisclaimerButton.vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { storeToRefs } from 'pinia';


export default /*#__PURE__*/_defineComponent({
  __name: 'QualityTV',
  props: {
    catalogueItem: null,
    title: null,
    offers: null,
    additionalContexts: null,
    seasonNumber: null,
    episodeNumber: null,
    swiperIndex: null,
    hardcodedShortName: null,
    displayPromotedTag: { type: Boolean, default: true },
    providerFilter: null,
    adServerCreative: null
  },
  setup(__props: any) {

const props = __props as { catalogueItem: CatalogueItem, title: TitleDetail, offers: Offer[], additionalContexts: SnowplowContext[], seasonNumber?: number | null, episodeNumber?: number | null, swiperIndex?: number, hardcodedShortName?: string | null, displayPromotedTag: boolean, providerFilter?: TitleFilter, adServerCreative?: AdServerQtvCreativeFragment };



const titleListContainer = ref<HTMLElement | null>(null);

const { tc, t } = useI18n();
const { country } = storeToRefs(useLanguageStore());

const qtvShortName = computed(() => props.hardcodedShortName ?? props.catalogueItem.shortName);

const qualityTVType = computed(() => qtvProviderType[qtvShortName.value]);

const qtvProviderPackage = computed(() => qtvTitles.value[0].offers[0].package);

const isFreeTrialQTV = computed(() => qualityTVType.value === 'FREE');

const hasWatchNowCTA = computed(() => !isFreeTrialQTV.value);

const bannerText = computed(() => {
	if (country.value === 'ES' && qtvShortName.value === 'sst') return 'Ve a SkyShowtime ahora';

	if (qualityTVType.value === 'SVOD')
		return t('WEBAPP_PROVIDER_QUALITY_TV_STREAM', { provider: qtvProviderPackage.value.clearName });
	if (qualityTVType.value === 'TVOD')
		return t('WEBAPP_PROVIDER_QUALITY_TV_RENT_OR_BUY', { provider: qtvProviderPackage.value.clearName });
	return t('WEBAPP_PROVIDER_QUALITY_TV_BANNER', { provider: qtvProviderPackage.value.clearName });
});

const titlesShownCount = computed(() => {
	const containerWidth = titleListContainer.value?.clientWidth || 0;
	const posterWidth = 80;
	const gap = 8;
	// in order to count the number visible in the viewport we divide the width of the container by the width of the poster + gap
	return Math.round(containerWidth / (posterWidth + gap));
});
// A map that caches visibility callbacks for titles by their unique ID.
// This ensures each title has a stable, reusable callback reference.
// This is important because impression tracking causes a re-render of the component, and a loop of new callbacks would be created otherwise.
const observerCallbacks = new Map<string, (isVisible: boolean) => void>();

const { qtvTitles, onVisibilityChanged, clickoutUrls, ctaClickoutUrl, onCTAVisibilityChanged, retailPrice } = useQTV({
	shortName: () => qtvShortName.value,
	sourceTitle: () => props.title,
	titlesShownCount: titlesShownCount,
	additionalContexts: () => props.additionalContexts,
	seasonNumber: () => props.seasonNumber,
	episodeNumber: () => props.episodeNumber,
	swiperIndex: () => props.swiperIndex,
	titleCount: 10,
	providerFilter: () => props.providerFilter,
	adServerCreative: () => props.adServerCreative,
});

const freeTrialQTVCTA = computed(() => {
	if (['AU', 'CA'].includes(country.value)) {
		return tc('WEBAPP_WATCHNOW');
	}

	return tc('WEBAPP_QUALITY_TV_CTA');
});

const { isModalProvider, openPlayerModal } = useTitlePlayerModal(qtvShortName);

const hideTitlesFade = ref(false);

const intersectionOptionsForFade = {
	once: false,
	throttle: 0,
	intersection: {
		root: null,
		rootMargin: '0px 0px 0px 0px',
		threshold: 0.95,
	},
};

function toggleFade(isVisible: boolean, index: number) {
	if (index === qtvTitles.value.length - 1) hideTitlesFade.value = isVisible;
}

function imageUrl(titlePosterUrl: string | null | undefined) {
	return Vue.filter('Thumbor')({
		url: titlePosterUrl,
		imageType: 'poster',
	} as ThumborOptions);
}

function imbdScore(title: { content: { scoring: { imdbScore?: number | null } } }) {
	return title.content.scoring?.imdbScore?.toFixed(1);
}

async function handleQTVClick(qtvTitle: QTVSimilarTitle, url: ClickoutUrl) {
	if (!isModalProvider.value || !url) return;
	await openPlayerModal({
		offer: qtvTitle.offers[0],
		title: qtvTitle,
		clickoutUrl: url,
		rentalOptions: { overrideRedirectUrl: true },
	});
}

/**
 * Returns a stable visibility callback for a given title.
 * If one doesn't already exist for the title ID, it creates and stores it.
 * This memoization ensures consistent identity (important to avoid re-rendering issues)
 */

function getVisibilityCallback(title: QTVSimilarTitle, index: number) {
	if (!observerCallbacks.has(title.id)) {
		observerCallbacks.set(title.id, isVisible => onVisibilityChanged(title, index, isVisible));
	}
	return observerCallbacks.get(title.id)!;
}

function getCTAVisiblityCalback() {
	if (!observerCallbacks.has('cta')) {
		observerCallbacks.set('cta', isVisible => {
			onCTAVisibilityChanged(isVisible);
		});
	}
	return observerCallbacks.get('cta')!;
}

function handleCTAObservation() {
	const stableCallback = getCTAVisiblityCalback();
	return {
		...mrcObservableOptions,
		callback: stableCallback,
	};
}

function handleObservation(title: QTVSimilarTitle, index: number) {
	const stableCallback = getVisibilityCallback(title, index);
	return {
		...mrcObservableOptions,
		callback: stableCallback,
	};
}

return { __sfc: true,props, titleListContainer, tc, t, country, qtvShortName, qualityTVType, qtvProviderPackage, isFreeTrialQTV, hasWatchNowCTA, bannerText, titlesShownCount, observerCallbacks, qtvTitles, onVisibilityChanged, clickoutUrls, ctaClickoutUrl, onCTAVisibilityChanged, retailPrice, freeTrialQTVCTA, isModalProvider, openPlayerModal, hideTitlesFade, intersectionOptionsForFade, toggleFade, imageUrl, imbdScore, handleQTVClick, getVisibilityCallback, getCTAVisiblityCalback, handleCTAObservation, handleObservation, HiddenHorizontalScrollbar, ProviderIcon, faPlay, ClickoutAnchor, DisclaimerButton }
}

})